import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import SimpleDropdownPopup from '../ui/SimpleDropdownPopup';
import { UniversalDropdownPopup } from '../ui/UniversalDropdownPopup';
import { useGlobalData } from '../../contexts/GlobalDataContext';
import { useDropdownOptions } from '../../hooks/useDropdownOptions';
import { ECRCode } from '../../types/ecrCode';
import { Brand } from '../../types/brand';
import { Label } from '../../types/label';
import { BackendChangeIndicator } from '../common/BackendChangeIndicator';

// Hilfsfunktion für einheitliche "Kein Wert" Anzeige
const renderEmptyValue = (value: any, fallback = 'Kein Wert') => {
  if (!value || value === '' || value === null || value === undefined) {
    return <span className="text-xs text-gray-500 italic">{fallback}</span>;
  }
  return value;
};

// Hilfsfunktion um Dropdown-Werte in ausgeschriebene Labels zu konvertieren
const getDropdownLabel = (value: string | undefined | null, options: any[]) => {
  if (!value) return value;
  if (!options || !Array.isArray(options)) return value;
  
  const option = options.find(opt => opt && opt.value === value);
  const result = option ? option.label_DE : value;
  
  // Debug logging für Problemdiagnose
  if (!option && value) {
    console.log(`[getDropdownLabel] No match found for value "${value}" in options:`, options.map(o => ({ value: o?.value, label: o?.label_DE })));
  }
  
  return result;
};

interface ProductDataTabProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  // Preloaded data to avoid reloading
  preloadedEcrCodes?: ECRCode[];
  preloadedBrands?: Brand[];
  preloadedLabels?: Label[];
  isSupplierMode?: boolean;
  // **NEW: Enhanced change detection**
  originalData?: any;
  currentData?: any;
  articleNumber?: string;
}

export const ProductDataTab: React.FC<ProductDataTabProps> = ({ 
  data, 
  mode, 
  onChange, 
  preloadedEcrCodes = [],
  preloadedBrands = [],
  preloadedLabels = [],
  isSupplierMode = false,
  originalData,
  currentData,
  articleNumber
}) => {
  // Use global data context - no need to load data individually anymore
  const { ecrCodes, brands, labels } = useGlobalData();
  
  // Load dropdown options for displaying labels
  const { options: contentUnitOptions } = useDropdownOptions('contentUnit');
  const { options: unitOptions } = useDropdownOptions('unit');
  const { options: countryOptions } = useDropdownOptions('country');
  const { options: dangerousGoodsOptions } = useDropdownOptions('dangerousGoods');
  const { options: baseUnitOptions } = useDropdownOptions('baseUnit');
  const { options: baseComparisonUnitCodeOptions } = useDropdownOptions('baseComparisonUnitCode');

  // Debug: Log loaded options
  useEffect(() => {
    console.log('[ArticleDetailEinkauf] Dropdown options loaded:', {
      contentUnitOptions: contentUnitOptions?.length || 0,
      unitOptions: unitOptions?.length || 0,
      countryOptions: countryOptions?.length || 0
    });
    
    if (data) {
      console.log('[ArticleDetailEinkauf] Article data:', {
        contentUnit: data.contentUnit,
        unit: data.unit,
        countryOfOrigin: data.countryOfOrigin
      });
    }
  }, [contentUnitOptions, unitOptions, countryOptions, data]);

  // **ENHANCED UTILITY FUNCTION FOR RENDERING FIELDS WITH CHANGE HIGHLIGHTING**
  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Change tracking only for Admin (not Supplier mode)
    if (isSupplierMode || !articleNumber) {
      return content;
    }
    
    return (
      <BackendChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        articleNumber={articleNumber}
      >
        {content}
      </BackendChangeIndicator>
    );
  };

  // Standard-Einheiten definieren
  const defaultUnits = [
    { id: '1', unitDescriptor: 'Basiseinheit (Stück)', packagingUnit: '', quantityOfBaseUnits: '0', isTradeItemBaseUnit: true },
    { id: '2', unitDescriptor: 'Karton', packagingUnit: '', quantityOfBaseUnits: '0' },
    { id: '3', unitDescriptor: 'Lage', packagingUnit: '', quantityOfBaseUnits: '0' },
    { id: '4', unitDescriptor: 'Palette', packagingUnit: '', quantityOfBaseUnits: '0' }
  ];

  // Initialisierung der Units beim ersten Laden
  useEffect(() => {
    if (data && (!data.units || data.units.length === 0)) {
      onChange('units', defaultUnits);
    }
  }, [data]);

  return (
    <div className="space-y-6">
      
      {/* **PRODUKTINFORMATIONEN** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Produktinformationen</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Bezeichnung 1 */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bezeichnung 1</label>
            {renderFieldWithHistory('Bezeichnung 1', 'wedlDesignation1', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.wedlDesignation1 || ''}
                  onChange={(e) => onChange('wedlDesignation1', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.wedlDesignation1)}</p>
              )
            ))}
          </div>

          {/* Bezeichnung 2 */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bezeichnung 2</label>
            {renderFieldWithHistory('Bezeichnung 2', 'wedlDesignation2', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.wedlDesignation2 || ''}
                  onChange={(e) => onChange('wedlDesignation2', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.wedlDesignation2)}</p>
              )
            ))}
          </div>

          {/* GTIN */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">GTIN</label>
            {renderFieldWithHistory('GTIN', 'gtin', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.gtin || ''}
                  onChange={(e) => onChange('gtin', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.gtin)}</p>
              )
            ))}
          </div>

          {/* Externe Artikelnummer */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Externe Artikelnummer</label>
            {renderFieldWithHistory('Externe Artikelnummer', 'supplierAssignedId', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.supplierAssignedId || ''}
                  onChange={(e) => onChange('supplierAssignedId', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.supplierAssignedId)}</p>
              )
            ))}
          </div>

          {/* Intrastatnummer */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Intrastatnummer</label>
            {renderFieldWithHistory('Intrastat-Nummer', 'intrastatNumber', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.intrastatNumber || ''}
                  onChange={(e) => onChange('intrastatNumber', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.intrastatNumber)}</p>
              )
            ))}
          </div>

          {/* KreditorNummer */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">KreditorNummer</label>
            {renderFieldWithHistory('Kreditoren-Nr.', 'creditorNumber', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.creditorNumber || ''}
                  onChange={(e) => onChange('creditorNumber', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.creditorNumber)}</p>
              )
            ))}
          </div>

          {/* Marke */}
          <div>
            {renderFieldWithHistory('Marke', 'brand', (
              mode === 'edit' ? (
                <UniversalDropdownPopup
                  category="brand"
                  value={data?.brand || ''}
                  onChange={(value: string | string[]) => onChange('brand', value)}
                  label="Marke"
                  placeholder="Marke auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Marke</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.brand)}</p>
                </div>
              )
            ))}
          </div>

          {/* ECR Warengruppe */}
          <div>
            {renderFieldWithHistory('ECR-Code', 'ecrCode', (
              <>
                {mode === 'edit' ? (
                  <UniversalDropdownPopup
                    category="ecrCode"
                    value={data?.ecrCode || ''}
                    onChange={(value: string | string[]) => onChange('ecrCode', value)}
                    label="ECR Warengruppe"
                    placeholder="ECR-Code auswählen..."
                    allowAdminMode={!isSupplierMode}
                  />
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">ECR Warengruppe</label>
                    <div className="py-2">
                      {data?.ecrCode ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {data.ecrCode}
                          </span>
                        </div>
                      ) : (
                        <p className="text-gray-900 text-sm">{renderEmptyValue(null, 'Keine ECR Warengruppe')}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            ))}
          </div>

          {/* Linie */}
          <div>
            {renderFieldWithHistory('Linie', 'productLine', (
              mode === 'edit' ? (
                <UniversalDropdownPopup
                  category="line"
                  value={data?.productLine || ''}
                  onChange={(value: string | string[]) => onChange('productLine', value)}
                  label="Linie"
                  placeholder="Linie auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Linie</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.productLine)}</p>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* **INHALT UND EINHEITEN** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Inhalt und Einheiten</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Inhalt */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Inhalt</label>
            {renderFieldWithHistory('Inhalt', 'wedlContent', (
              mode === 'edit' ? (
                <input
                  type="number"
                  step="0.01"
                  value={data?.wedlContent || ''}
                  onChange={(e) => onChange('wedlContent', parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.wedlContent)}</p>
              )
            ))}
          </div>

          {/* Inhaltseinheit */}
          <div>
            {renderFieldWithHistory('Inhaltseinheit', 'wedlContentUnit', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="unit"
                  value={data?.wedlContentUnit || ''}
                  onChange={(value: string) => onChange('wedlContentUnit', value)}
                  label="Inhaltseinheit"
                  placeholder="Einheit auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Inhaltseinheit</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.wedlContentUnit, contentUnitOptions))}</p>
                </div>
              )
            ))}
          </div>

          {/* Werbeeinheit */}
          <div>
            {renderFieldWithHistory('Werbeeinheit', 'wedlUnit', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="unit"
                  value={data?.wedlUnit || ''}
                  onChange={(value: string) => onChange('wedlUnit', value)}
                  label="Werbeeinheit"
                  placeholder="Einheit auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Werbeeinheit</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.wedlUnit, unitOptions))}</p>
                </div>
              )
            ))}
          </div>

          {/* Labels */}
          <div>
            {renderFieldWithHistory('Labels', 'labels', (
              mode === 'edit' ? (
                <UniversalDropdownPopup
                  category="labels"
                  value={Array.isArray(data?.labels) ? data.labels : (data?.labels ? [data.labels] : [])}
                  onChange={(selectedLabels) => onChange('labels', selectedLabels)}
                  label="Labels"
                  placeholder="Labels auswählen..."
                  isMultiSelect={true}
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Labels</label>
                  <div className="py-2">
                    {data?.labels && data.labels.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(data.labels) ? data.labels : [data.labels]).map((labelValue: string, index: number) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                          >
                            {labelValue}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-900 text-sm">{renderEmptyValue(null, 'Keine Labels')}</p>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Herkunftsland */}
          <div>
            {renderFieldWithHistory('Herkunftsland', 'countryOfOrigin', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="country"
                  value={data?.countryOfOrigin || ''}
                  onChange={(value: string) => onChange('countryOfOrigin', value)}
                  label="Herkunftsland"
                  placeholder="Herkunftsland auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Herkunftsland</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.countryOfOrigin, countryOptions))}</p>
                </div>
              )
            ))}
          </div>

          {/* Gefahrgut */}
          <div>
            {renderFieldWithHistory('Gefahrgut', 'dangerousGoods', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="dangerousGoods"
                  value={data?.dangerousGoods || ''}
                  onChange={(value: string) => onChange('dangerousGoods', value)}
                  label="Gefahrgut"
                  placeholder="Gefahrgut auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Gefahrgut</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.dangerousGoods, dangerousGoodsOptions))}</p>
                </div>
              )
            ))}
          </div>

          {/* Min. Lauftage ab Wareneingang */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Min. Lauftage ab Wareneingang</label>
            {renderFieldWithHistory('Min. Lauftage ab Wareneingang', 'minTermIncoming', (
              mode === 'edit' ? (
                <input
                  type="number"
                  value={data?.minTermIncoming || ''}
                  onChange={(e) => onChange('minTermIncoming', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="Tage..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.minTermIncoming)}</p>
              )
            ))}
          </div>

          {/* Haltbarkeit in Tagen */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Haltbarkeit in Tagen</label>
            {renderFieldWithHistory('Haltbarkeit in Tagen', 'shelfLifeDays', (
              mode === 'edit' ? (
                <input
                  type="number"
                  value={data?.shelfLifeDays || ''}
                  onChange={(e) => onChange('shelfLifeDays', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="Tage..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.shelfLifeDays)}</p>
              )
            ))}
          </div>

          {/* Min. Temperatur (°C) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Min. Temperatur (°C)</label>
            {renderFieldWithHistory('Min. Temperatur (°C)', 'stockTempMin', (
              mode === 'edit' ? (
                <input
                  type="number"
                  step="0.1"
                  value={data?.stockTempMin || ''}
                  onChange={(e) => onChange('stockTempMin', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="°C..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.stockTempMin)}</p>
              )
            ))}
          </div>

          {/* Max. Temperatur (°C) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max. Temperatur (°C)</label>
            {renderFieldWithHistory('Max. Temperatur (°C)', 'stockTempMax', (
              mode === 'edit' ? (
                <input
                  type="number"
                  step="0.1"
                  value={data?.stockTempMax || ''}
                  onChange={(e) => onChange('stockTempMax', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="°C..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.stockTempMax)}</p>
              )
            ))}
          </div>

          {/* Nettofüllmenge */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nettofüllmenge</label>
            {renderFieldWithHistory('Nettofüllmenge', 'netContent', (
              mode === 'edit' ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={data?.netContent || ''}
                    onChange={(e) => onChange('netContent', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                    placeholder="Menge..."
                  />
                  <select
                    value={data?.netContentUom || ''}
                    onChange={(e) => onChange('netContentUom', e.target.value)}
                    className="w-32 border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  >
                    <option value="">-</option>
                    <option value="Anzahl">Anzahl</option>
                    <option value="Becher">Becher</option>
                    <option value="Beutel">Beutel</option>
                    <option value="Bund">Bund</option>
                    <option value="Deziliter">Deziliter</option>
                    <option value="Dezimeter">Dezimeter</option>
                    <option value="Dosis">Dosis</option>
                    <option value="Eines">Eines</option>
                    <option value="Gebrauchseinheiten">Gebrauchseinheiten</option>
                    <option value="Gramm">Gramm</option>
                    <option value="Hektoliter">Hektoliter</option>
                    <option value="kg">kg</option>
                    <option value="Kilogramm">Kilogramm</option>
                    <option value="Kilometer">Kilometer</option>
                    <option value="Kit (Bausatz oder Set aus mehreren Komponenten)">Kit (Bausatz oder Set aus mehreren Komponenten)</option>
                    <option value="Kubikdezimeter">Kubikdezimeter</option>
                    <option value="Kubikmeter">Kubikmeter</option>
                    <option value="Kubikmillimeter">Kubikmillimeter</option>
                    <option value="Kubikzentimeter">Kubikzentimeter</option>
                    <option value="l">l</option>
                    <option value="Liter">Liter</option>
                    <option value="Meter">Meter</option>
                    <option value="Millibar">Millibar</option>
                    <option value="Milligramm">Milligramm</option>
                    <option value="Milliliter">Milliliter</option>
                    <option value="Millimeter">Millimeter</option>
                    <option value="Netz">Netz</option>
                    <option value="Paar">Paar</option>
                    <option value="Packung">Packung</option>
                    <option value="Packungen">Packungen</option>
                    <option value="Portion">Portion</option>
                    <option value="Quadratdezimeter">Quadratdezimeter</option>
                    <option value="Quadratmeter">Quadratmeter</option>
                    <option value="Quadratmillimeter">Quadratmillimeter</option>
                    <option value="Quadratzentimeter">Quadratzentimeter</option>
                    <option value="Rolle">Rolle</option>
                    <option value="Set (eine Anzahl zusammengefasster Komponenten)">Set (eine Anzahl zusammengefasster Komponenten)</option>
                    <option value="Stück">Stück</option>
                    <option value="Tablette">Tablette</option>
                    <option value="Tasse">Tasse</option>
                    <option value="Tassen">Tassen</option>
                    <option value="Tonne (metrische Tonne)">Tonne (metrische Tonne)</option>
                    <option value="Zentiliter">Zentiliter</option>
                    <option value="Zentimeter">Zentimeter</option>
                  </select>
                </div>
              ) : (
                <p className="text-gray-900 py-2 text-sm">
                  {data?.netContent && data?.netContentUom 
                    ? `${data.netContent} ${data.netContentUom}`
                    : renderEmptyValue(null)
                  }
                </p>
              )
            ))}
          </div>

          {/* Abtropfgewicht */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Abtropfgewicht</label>
            {renderFieldWithHistory('Abtropfgewicht', 'drainedWeight', (
              mode === 'edit' ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={data?.drainedWeight || ''}
                    onChange={(e) => onChange('drainedWeight', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                    placeholder="Gewicht..."
                  />
                  <select
                    value={data?.drainedWeightUom || ''}
                    onChange={(e) => onChange('drainedWeightUom', e.target.value)}
                    className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  >
                    <option value="">-</option>
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="l">l</option>
                    <option value="mg">mg</option>
                    <option value="t">t</option>
                  </select>
                </div>
              ) : (
                <p className="text-gray-900 py-2 text-sm">
                  {data?.drainedWeight && data?.drainedWeightUom 
                    ? `${data.drainedWeight} ${data.drainedWeightUom}`
                    : renderEmptyValue(null)
                  }
                </p>
              )
            ))}
          </div>

          {/* Mehrweg/Einweg */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Mehrweg/Einweg</label>
            {renderFieldWithHistory('Mehrweg/Einweg', 'reusableCode', (
              mode === 'edit' ? (
                <select
                  value={data?.reusableCode || ''}
                  onChange={(e) => onChange('reusableCode', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                >
                  <option value="">Auswählen...</option>
                  <option value="Einweg">Einweg</option>
                  <option value="Einweg mit Pfand">Einweg mit Pfand</option>
                  <option value="Einweg mit Pfand vorgesehen">Einweg mit Pfand vorgesehen</option>
                  <option value="kein Mehrweg/Einweg">kein Mehrweg/Einweg</option>
                  <option value="Mehrweg mit Pfand">Mehrweg mit Pfand</option>
                  <option value="Mehrweg ohne Pfand">Mehrweg ohne Pfand</option>
                </select>
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.reusableCode)}</p>
              )
            ))}
          </div>

          {/* Stück-Gewichtsartikel */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stück-Gewichtsartikel</label>
            {renderFieldWithHistory('Stück-Gewichtsartikel', 'pieceWeightArticle', (
              mode === 'edit' ? (
                <div className="flex items-center h-[36px]">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data?.pieceWeightArticle || false}
                      onChange={(e) => onChange('pieceWeightArticle', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-2 text-xs text-gray-700">
                      {data?.pieceWeightArticle ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                  </label>
                </div>
              ) : (
                <div className="py-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    data?.pieceWeightArticle 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {data?.pieceWeightArticle ? '✓ Aktiviert' : '✗ Deaktiviert'}
                  </span>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* **GRUNDPREIS-INFORMATIONEN** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Grundpreis-Informationen</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          {/* Grundpreisinhalt */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Grundpreisinhalt</label>
            {renderFieldWithHistory('Grundpreisinhalt', 'basePriceContent', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.basePriceContent || ''}
                  onChange={(e) => onChange('basePriceContent', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.basePriceContent)}</p>
              )
            ))}
          </div>

          {/* Grundeinheit */}
          <div>
            {renderFieldWithHistory('Grundeinheit', 'baseUnit', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="baseUnit"
                  value={data?.baseUnit || ''}
                  onChange={(value: string) => onChange('baseUnit', value)}
                  label="Grundeinheit"
                  placeholder="Grundeinheit auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Grundeinheit</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.baseUnit, baseUnitOptions))}</p>
                </div>
              )
            ))}
          </div>

          {/* Basis Vergleichs-Einheitencode */}
          <div>
            {renderFieldWithHistory('Basis Vergleichs-Einheitencode', 'baseComparisonUnitCode', (
              mode === 'edit' ? (
                <SimpleDropdownPopup
                  category="baseComparisonUnitCode"
                  value={data?.baseComparisonUnitCode || ''}
                  onChange={(value: string) => onChange('baseComparisonUnitCode', value)}
                  label="Basis Vergleichs-Einheitencode"
                  placeholder="Code auswählen..."
                  allowAdminMode={!isSupplierMode}
                />
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Basis Vergleichs-Einheitencode</label>
                  <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(getDropdownLabel(data?.baseComparisonUnitCode, baseComparisonUnitCodeOptions))}</p>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* **HERSTELLERINFORMATIONEN** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Herstellerinformationen</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Herstellername */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Herstellername</label>
            {renderFieldWithHistory('Herstellername', 'manufacturerName', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.manufacturerName || ''}
                  onChange={(e) => onChange('manufacturerName', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="Herstellername..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.manufacturerName)}</p>
              )
            ))}
          </div>

          {/* Hersteller GLN */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Hersteller GLN</label>
            {renderFieldWithHistory('Hersteller GLN', 'manufacturerGLN', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.manufacturerGLN || ''}
                  onChange={(e) => onChange('manufacturerGLN', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="GLN..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.manufacturerGLN)}</p>
              )
            ))}
          </div>

          {/* Inverkehrbringen Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Inverkehrbringen Name</label>
            {renderFieldWithHistory('Inverkehrbringen Name', 'contactName', (
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.contactName || ''}
                  onChange={(e) => onChange('contactName', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[36px]"
                  placeholder="Name..."
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.contactName)}</p>
              )
            ))}
          </div>

          {/* Inverkehrbringen Adresse */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Inverkehrbringen Adresse</label>
            {renderFieldWithHistory('Inverkehrbringen Adresse', 'contactAddress', (
              mode === 'edit' ? (
                <textarea
                  value={data?.contactAddress || ''}
                  onChange={(e) => onChange('contactAddress', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Adresse..."
                  rows={2}
                />
              ) : (
                <p className="text-gray-900 py-2 text-sm">{renderEmptyValue(data?.contactAddress)}</p>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
